/**
 * Storefront layout — Task 33.1 + Task 14.3 (dynamic CMS header) + Task 14.4 (dynamic CMS footer)
 * Light Header + Dark Footer + Bigger Logo
 */
import { Outlet, Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, User, Heart, Search, Facebook, Instagram, Twitter, Youtube, Linkedin, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useCartStore } from '@/stores/cartStore'
import { useLogout } from '@/hooks/useAuth'
import { useFeatures } from '@/hooks/useFeatures'
import { LanguageSwitcher } from '@/components/storefront/LanguageSwitcher'
import { CartDrawer } from '@/components/storefront/CartDrawer'
import { ScrollToTopButton } from '@/components/ui/ScrollToTopButton'
import { WhatsAppFloatingButton } from '@/components/storefront/WhatsAppFloatingButton'
import { SearchBar } from '@/components/storefront/SearchBar'
import { MobileMenu } from '@/components/layout/MobileMenu'
import { usePublicMenu, type MenuItem } from '@/hooks/usePublicMenu'
import { useCmsHeader } from '@/hooks/useCmsHeader'
import { useCmsFooter } from '@/hooks/useCmsFooter'
import { NewsletterSignup } from '@/components/storefront/NewsletterSignup'
import logoImg from '@/assets/logo.png'
import { getAdminPanelUrl } from '@/lib/adminUrl'

/** Helper to get the href for a menu item */
function getMenuItemHref(item: MenuItem): string {
  if (item.link_type === 'category') {
    return `/categories/${item.category_slug ?? item.link_target}`
  }
  if (item.link_type === 'custom_url') {
    return item.link_target ?? '/'
  }
  return item.link_target ?? '/'
}

/** Check if a URL is external */
function isExternalUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin
  } catch {
    return false
  }
}

/** Renders a menu link (internal or external) */
function MenuLink({ item, className }: { item: MenuItem; className?: string }) {
  const href = getMenuItemHref(item)
  const isExternal = item.link_type === 'custom_url' && item.link_target && isExternalUrl(item.link_target)

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {item.label}
      </a>
    )
  }
  return <Link to={href} className={className}>{item.label}</Link>
}

/** Navigation menu item with dropdown support for children */
function NavMenuItem({ item }: { item: MenuItem }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // No children — render a simple link
  if (!item.children || item.children.length === 0) {
    return (
      <MenuLink
        item={item}
        className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
      />
    )
  }

  // Has children — render dropdown
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors py-2 px-1 relative group"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? 'rotate-180 text-primary-600' : ''}`} />
        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 min-w-[220px] rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl p-2 shadow-[0_10px_40px_rgba(0,0,0,0.08)] z-50 animate-fade-in-up origin-top-left"
          onMouseLeave={() => setOpen(false)}
        >
          {item.children.map((child) => (
            <NavDropdownItem key={child.id} item={child} depth={1} onClose={() => setOpen(false)} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Dropdown item — supports nested submenus */
function NavDropdownItem({ item, depth, onClose }: { item: MenuItem; depth: number; onClose: () => void }) {
  const [subOpen, setSubOpen] = useState(false)
  const hasChildren = item.children && item.children.length > 0

  if (!hasChildren) {
    return (
      <MenuLink
        item={item}
        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
      />
    )
  }

  // Has sub-children — show flyout submenu
  return (
    <div
      className="relative"
      onMouseEnter={() => setSubOpen(true)}
      onMouseLeave={() => setSubOpen(false)}
    >
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
        onClick={() => setSubOpen((o) => !o)}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
      </button>

      {subOpen && (
        <div className="absolute left-full top-0 ml-1.5 min-w-[200px] rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl p-2 shadow-[0_10px_40px_rgba(0,0,0,0.08)] z-50 animate-fade-in-up origin-top-left">
          {item.children!.map((child) => (
            <NavDropdownItem key={child.id} item={child} depth={depth + 1} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  )
}

/** Returns a lucide-react icon component for a given social platform name */
function SocialIcon({ platform }: { platform: string }) {
  const name = platform.toLowerCase()
  if (name.includes('facebook')) return <Facebook className="h-5 w-5" />
  if (name.includes('instagram')) return <Instagram className="h-5 w-5" />
  if (name.includes('twitter') || name.includes('x')) return <Twitter className="h-5 w-5" />
  if (name.includes('youtube')) return <Youtube className="h-5 w-5" />
  if (name.includes('linkedin')) return <Linkedin className="h-5 w-5" />
  // Fallback: show platform name as text
  return <span className="text-sm">{platform}</span>
}

export default function StorefrontLayout() {
  const { t } = useTranslation('common')
  const { user, isAuthenticated } = useAuthStore()
  const { cart, toggleCart } = useCartStore()
  const logout = useLogout()
  const navigate = useNavigate()
  const { features } = useFeatures()

  // Fetch CMS header configuration
  const { headerConfig } = useCmsHeader()

  // Use menu_slug from CMS header config, fallback to 'main-menu'
  const menuSlug = headerConfig?.menu_slug ?? 'main-menu'
  const { menu } = usePublicMenu(menuSlug)
  const { footer } = useCmsFooter()

  // Determine logo source: use CMS-configured logo_url, fallback to static asset
  const headerLogoSrc = headerConfig?.logo_url ? headerConfig.logo_url : logoImg

  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const accountRef = useRef<HTMLDivElement>(null)
  const fallbackNavItems = [
    { label: 'Home', to: '/' },
    { label: 'Products', to: '/products' },
    ...(features.enable_contact_form ? [{ label: 'Contact', to: '/contact' }] : []),
  ]

  const cartCount = cart?.items.reduce((sum, i) => sum + i.quantity, 0) ?? 0

  // Close account menu on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  async function handleLogout() {
    await logout.mutateAsync()
    navigate('/auth/login')
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* ── Announcement Bar (conditionally rendered from CMS) ── */}
      {headerConfig?.announcement_bar_enabled && headerConfig.announcement_bar_text && (
        <div className="bg-gradient-to-r from-primary-700 via-primary-600 to-primary-800 text-white text-center text-xs tracking-wider font-semibold py-2 px-4 shadow-sm border-b border-white/10 uppercase">
          {headerConfig.announcement_bar_text}
        </div>
      )}

      {/* ── Premium Sticky Header ── */}
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur-md transition-all duration-300 shadow-[0_4px_30px_rgba(0,0,0,0.02)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-28 items-center justify-between gap-6">
            {/* Logo - dynamic from CMS, fallback to static asset */}
            <Link to="/" className="flex-shrink-0 transition-transform duration-300 hover:scale-[1.03]">
              <img 
                src={headerLogoSrc} 
                alt={t('app_name')} 
                className="h-24 w-auto object-contain"
              />
            </Link>

            {/* Main Navigation — desktop (dynamic from menu API, with fallback) */}
            <nav className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
              {menu && menu.items.length > 0 ? (
                menu.items.map((item) => (
                  <NavMenuItem key={item.id} item={item} />
                ))
              ) : (
                fallbackNavItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="text-sm font-semibold text-gray-700 hover:text-primary-600 transition-colors relative group py-2 px-1"
                  >
                    {item.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-full"></span>
                  </Link>
                ))
              )}
            </nav>

            {/* Search bar — desktop */}
            <div className="hidden flex-1 max-w-sm lg:block relative z-50">
              <SearchBar />
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-3">
              {/* Search icon — mobile */}
              <button
                className="rounded-full p-3 text-gray-600 border border-gray-100 bg-gray-50/50 hover:bg-primary-50/50 hover:border-primary-100/50 hover:text-primary-600 transition-all hover:scale-105 shadow-sm lg:hidden"
                aria-label={t('search')}
                onClick={() => navigate('/search')}
              >
                <Search className="h-5 w-5" />
              </button>

              {/* Wishlist */}
              {isAuthenticated && features.enable_wishlist && (
                <Link
                  to="/account/wishlist"
                  className="rounded-full p-3 text-gray-600 border border-gray-100 bg-gray-50/50 hover:bg-primary-50/50 hover:border-primary-100/50 hover:text-primary-600 transition-all hover:scale-105 shadow-sm"
                  aria-label={t('wishlist')}
                >
                  <Heart className="h-5 w-5" />
                </Link>
              )}

              {/* Cart */}
              <button
                onClick={toggleCart}
                className="relative rounded-full p-3 text-gray-600 border border-gray-100 bg-gray-50/50 hover:bg-primary-50/50 hover:border-primary-100/50 hover:text-primary-600 transition-all hover:scale-105 shadow-sm"
                aria-label={`${t('cart')} (${cartCount})`}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white shadow-sm animate-pulse">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>

              {/* Account menu */}
              <div className="relative" ref={accountRef}>
                <button
                  onClick={() => setAccountMenuOpen((o) => !o)}
                  className="flex items-center gap-1 rounded-full p-3 text-gray-600 border border-gray-100 bg-gray-50/50 hover:bg-primary-50/50 hover:border-primary-100/50 hover:text-primary-600 transition-all hover:scale-105 shadow-sm"
                  aria-label={t('account')}
                  aria-expanded={accountMenuOpen}
                >
                  <User className="h-5 w-5" />
                </button>

                {accountMenuOpen && (
                  <div className="absolute right-0 mt-3 w-56 z-50 rounded-2xl border border-gray-100 bg-white/95 backdrop-blur-xl p-2 shadow-[0_10px_40px_rgba(0,0,0,0.08)] animate-fade-in-up origin-top-right">
                    {isAuthenticated ? (
                      <>
                        <div className="border-b border-gray-100 px-4 py-3 mb-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                          <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                        </div>
                        <Link
                          to="/account"
                          className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('account')}
                        </Link>
                        <Link
                          to="/account/orders"
                          className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('orders')}
                        </Link>
                        {features.enable_wishlist && (
                        <Link
                          to="/account/wishlist"
                          className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('wishlist')}
                        </Link>
                        )}
                        {(user?.role === 'admin' || user?.role === 'super_admin') && (
                          <a
                            href={getAdminPanelUrl()}
                            className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                            onClick={() => setAccountMenuOpen(false)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Admin Panel
                          </a>
                        )}
                        <button
                          onClick={handleLogout}
                          className="block w-full rounded-xl px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors mt-1"
                        >
                          {t('logout')}
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/auth/login"
                          className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('login')}
                        </Link>
                        <Link
                          to="/auth/register"
                          className="block rounded-xl px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50/50 hover:text-primary-600 transition-colors"
                          onClick={() => setAccountMenuOpen(false)}
                        >
                          {t('register')}
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile menu toggle — category navigation */}
              <MobileMenu slug={menuSlug} />
            </div>
          </div>

          {/* Mobile search */}
          <div className="pb-3 lg:hidden">
            <SearchBar />
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ── Dark Footer (dynamic from CMS API) ── */}
      <footer className="bg-gray-900 text-gray-300 border-t border-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {/* Brand column — always shown with bigger logo */}
            <div>
              <img 
                src={logoImg} 
                alt={t('app_name')} 
                className="h-20 w-auto mb-4 brightness-125 hover:scale-105 transition-transform duration-300"
              />
              <p className="text-sm text-gray-400 mb-6">
                Handcrafted with love. Unique arts and handicrafts for every home.
              </p>
              
              {/* Social media links — moved to bottom of logo column */}
              {footer && footer.social_links.length > 0 && (
                <div className="flex items-center gap-4">
                  {footer.social_links.map((social, idx) => (
                    <a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                      aria-label={social.platform}
                    >
                      <SocialIcon platform={social.platform} />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Dynamic footer columns from CMS config */}
            {footer && footer.columns.length > 0 ? (
              footer.columns.map((column, idx) => (
                <div key={idx}>
                  <h3 className="text-sm font-semibold text-white mb-3">{column.heading}</h3>
                  <ul className="space-y-2">
                    {column.links.map((link, linkIdx) => (
                      <li key={linkIdx}>
                        {link.url.startsWith('/') ? (
                          <Link to={link.url} className="text-sm hover:text-white transition-colors">
                            {link.label}
                          </Link>
                        ) : (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm hover:text-white transition-colors"
                          >
                            {link.label}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              /* Fallback columns when no CMS config */
              <>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3">Account</h3>
                  <ul className="space-y-2">
                    <li><Link to="/account" className="text-sm hover:text-white transition-colors">My Account</Link></li>
                    <li><Link to="/account/orders" className="text-sm hover:text-white transition-colors">Orders</Link></li>
                    {features.enable_wishlist && (
                      <li><Link to="/account/wishlist" className="text-sm hover:text-white transition-colors">Wishlist</Link></li>
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3">Support</h3>
                  <ul className="space-y-2">
                    {features.enable_returns && (
                      <li><Link to="/account/returns" className="text-sm hover:text-white transition-colors">Returns</Link></li>
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-3">Preferences</h3>
                  <div className="mt-3">
                    <LanguageSwitcher />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Newsletter signup (conditionally rendered) */}
          {footer?.newsletter_enabled && (
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <label htmlFor="newsletter-email" className="text-sm text-gray-400">
                Subscribe to our newsletter
              </label>
              <NewsletterSignup />
            </div>
          )}

          {/* Copyright text */}
          <div className="mt-8 border-t border-gray-800 pt-6 text-center text-sm text-gray-500">
            {footer?.copyright_text
              ? footer.copyright_text
              : `© ${new Date().getFullYear()} Arts & Handicrafts. All rights reserved.`}
          </div>
        </div>
      </footer>

      {/* Cart drawer */}
      <CartDrawer />

      <WhatsAppFloatingButton />
      <ScrollToTopButton stackedAboveFloatingWidget={features.enable_whatsapp_button} />
    </div>
  )
}